import { useEffect, useRef } from 'react';
import { createFileRoute, notFound } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { isUuid } from '@leanin/shared';
import { trackGroupViewed } from '@/lib/analytics/events';
import { GROUP_SLUG_RE } from '@/lib/group-url';
import { isNotFoundApiError } from '@/lib/api-error';
import { NotFoundPage } from '@/components/shell/NotFoundPage';
import { useMember, memberPhotoUrl } from '@/lib/use-session';
import {
  CircleDetailView,
  type CircleTabId,
  type CircleViewerRole,
} from '@/components/circles/CircleDetailView';
import {
  adaptEventSummary,
  type EventCardData,
} from '@/components/events/event-adapters';
import { useEventAdapterContext } from '@/components/events/event-adapters.client';
import {
  groupKeys,
  useInfiniteGroupMembers,
  type CircleDetailResponse,
  type GroupDetail,
} from '@/lib/queries/groups';
import { useInfiniteFeed } from '@/lib/queries/feed';

const VALID_TABS: ReadonlyArray<CircleTabId> = [
  'overview',
  'feed',
  'members',
  'events',
  'resources',
];

type Search = { tab?: CircleTabId; compose?: true; join?: true };

function toTabId(s: unknown): CircleTabId {
  if (typeof s === 'string' && (VALID_TABS as ReadonlyArray<string>).includes(s)) {
    return s as CircleTabId;
  }
  return 'overview';
}

// `?compose=1` opens the Feed tab's composer on landing (join-link "Say hello"
// step). Accept the same truthy forms the /feed route does; a bare compose
// intent implies the Feed tab, so it lands there rather than the overview.
// `?join=1` opens the Request-to-Join modal on landing — set by
// /auth/finishing when a referral signup's auto-join was refused because the
// group requires the intake form.
function toFlag(s: unknown): boolean {
  return s === '1' || s === 1 || s === true || s === 'true';
}

export const Route = createFileRoute('/_public/circles/$id/')({
  validateSearch: (raw: Record<string, unknown>): Search => {
    const compose = toFlag(raw.compose);
    const join = toFlag(raw.join);
    const tab = compose && raw.tab === undefined ? 'feed' : toTabId(raw.tab);
    return {
      tab,
      ...(compose ? { compose: true } : {}),
      ...(join ? { join: true } : {}),
    };
  },
  beforeLoad: ({ params }) => {
    // The detail GET resolves slugs as well as uuids; anything else is a 404.
    if (!isUuid(params.id) && !GROUP_SLUG_RE.test(params.id)) throw notFound();
  },
  component: CircleDetailPage,
});

// Top-level route component. Fires only the access-determining query, then
// renders CircleDetailView for all access levels — visitor, member, leader.
// Visitors receive null membersData/feedItems/upcomingMeetings; member-only
// queries are never fired for them.
//
// Anonymous viewers (no session — this route mounts under `_public`) get the
// visitor shape from the server (`access: 'visitor'`) with `viewer` set to
// null. CircleDetailView swaps the Request-to-Join CTA for "Sign in to Join"
// (→ /auth/signup) when viewer is null; member-only tabs stay gated.
function CircleDetailPage() {
  const { id } = Route.useParams();
  const navigate = Route.useNavigate();
  const queryClient = useQueryClient();

  // Resolver: the detail GET is the only endpoint that accepts a slug, so
  // this query keys by the raw route param and yields the canonical uuid.
  // For uuid params it shares the display query's key (one cache entry).
  const refQuery = useQuery(groupKeys.circle.detail(id));
  const circleUuid = refQuery.data?.circle.id ?? (isUuid(id) ? id : null);

  // Display query — always uuid-keyed, because every mutation-side cache
  // patch and invalidation (lib/mutations/groups.ts, lib/cache/
  // group-invalidation.ts) targets `detail(uuid)` and must reach the page
  // even when it's slug-addressed. Seeded from the resolver's data so a
  // slug arrival renders in one load. Everything downstream receives the
  // uuid (`data.circle.id`); the member-only sub-resource endpoints are
  // uuid-only.
  const detailQuery = useQuery({
    ...groupKeys.circle.detail(circleUuid ?? ''),
    enabled: circleUuid !== null,
    ...(refQuery.data !== undefined ? { initialData: refQuery.data } : {}),
  });
  const data: CircleDetailResponse | undefined = detailQuery.data;

  // Canonical URL: the slug is the address-bar form. A uuid arrival (all
  // internal links use uuids) is replaced with the slug once the detail
  // loads. The slug-keyed resolver entry is seeded with the same data
  // first, so the remount resolves synchronously — no visible reload; the
  // display and child queries are uuid-keyed and don't re-key at all.
  useEffect(() => {
    if (data === undefined) return;
    const slug = data.circle.slug;
    if (slug === null || slug === id) return;
    queryClient.setQueryData(groupKeys.circle.detail(slug).queryKey, data);
    void navigate({
      to: '/circles/$id',
      params: { id: slug },
      search: (current) => current,
      replace: true,
    });
  }, [data, id, navigate, queryClient]);

  // Once per circle, only after the canonical-slug replace above has settled
  // — a uuid arrival remounts this component under the slug param, and firing
  // on both mounts would double-count a single view.
  const viewedCircleIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (data === undefined) return;
    if (data.circle.slug !== null && data.circle.slug !== id) return;
    if (viewedCircleIdRef.current === data.circle.id) return;
    viewedCircleIdRef.current = data.circle.id;
    trackGroupViewed({
      group_type: 'circle',
      is_member: data.access !== 'visitor',
      is_leader: data.access === 'leader',
    });
  }, [data, id]);

  // A slug arrival fails on the resolver (the display query never enables),
  // so check both.
  if (isNotFoundApiError(refQuery.error) || isNotFoundApiError(detailQuery.error))
    return <NotFoundPage />;
  if ((refQuery.isError || detailQuery.isError) && !detailQuery.data)
    throw notFound();

  if (data === undefined) {
    return <CircleDetailLoadingShell id={id} />;
  }

  if (data.access === 'visitor') {
    return <CircleVisitorDetail id={data.circle.id} detail={data} />;
  }

  return (
    <AuthedCircleDetail
      id={data.circle.id}
      circle={data.circle}
      access={data.access}
    />
  );
}

function CircleDetailLoadingShell({ id }: { id: string }) {
  const me = useMember();
  return (
    <CircleDetailView
      circleId={id}
      detail={null}
      membersData={null}
      feedItems={null}
      upcomingMeetings={null}
      conversationId={undefined}
      viewerRole={me === null ? null : 'member'}
      viewer={
        me === null
          ? null
          : {
              id: me.id,
              name: `${me.firstName} ${me.lastName}`.trim(),
              photoUrl: memberPhotoUrl(me) ?? null,
            }
      }
    />
  );
}

function CircleVisitorDetail({
  id,
  detail,
}: {
  readonly id: string;
  readonly detail: Extract<CircleDetailResponse, { access: 'visitor' }>;
}) {
  const me = useMember();
  return (
    <CircleDetailView
      circleId={id}
      detail={detail.circle}
      membersData={null}
      feedItems={null}
      upcomingMeetings={null}
      conversationId={undefined}
      viewerRole={null}
      viewer={
        me === null
          ? null
          : {
              id: me.id,
              name: `${me.firstName} ${me.lastName}`.trim(),
              photoUrl: memberPhotoUrl(me) ?? null,
            }
      }
    />
  );
}

// Member / leader experience. Mounts only when access is known — visitors
// never pay for these queries.
function AuthedCircleDetail({
  id,
  circle,
  access,
}: {
  readonly id: string;
  readonly circle: GroupDetail;
  readonly access: 'member' | 'leader';
}) {
  const me = useMember();
  const eventCtx = useEventAdapterContext();
  const { compose, join } = Route.useSearch();
  const isLeader = access === 'leader';
  const viewerRole: CircleViewerRole = isLeader ? 'leader' : 'member';

  // Circle members feed the synchronous `circle` VM (aside preview + leader
  // derivation) as well as the Members tab, so the roster query stays
  // unfiltered: search filters the loaded pages client-side inside
  // GroupMembersTab (its default client mode). Server-driven search would
  // re-key this query and corrupt the aside/leader derivation during a
  // search — unlike networks, whose aside reads a separate leaders query.
  const membersQuery = useInfiniteGroupMembers(id, { pageSize: 50 });
  const feedQuery = useInfiniteFeed({ scope: `group:${id}` });
  const eventsQuery = useQuery(
    groupKeys.circle.events(id, { window: 'upcoming', pageSize: 25 }),
  );
  const conversationQuery = useQuery(groupKeys.circle.conversation(id));
  const joinRequestsQuery = useQuery({
    ...groupKeys.circle.joinRequests(id),
    enabled: isLeader,
  });
  const members = membersQuery.data?.pages.flatMap((p) => p.items) ?? null;
  const feedItems = feedQuery.data?.pages.flatMap((p) => p.items) ?? null;
  const conversation = conversationQuery.data;

  const upcomingMeetings: EventCardData[] | null = (() => {
    const items = eventsQuery.data?.items;
    if (items === undefined) return null;
    return items
      .filter((e) => e.cancelledAt === null)
      .slice()
      .sort((a, b) => a.startsAt.localeCompare(b.startsAt))
      .map((e) => adaptEventSummary(e, eventCtx));
  })();

  // The sidebar join-requests card now consumes the rich MembershipRequestView
  // directly (intake answers + "View application"), so pass the raw pending
  // slice through instead of a lossy summary projection.
  const joinRequests = isLeader ? (joinRequestsQuery.data?.items ?? []) : [];

  return (
    <CircleDetailView
      circleId={id}
      detail={circle}
      membersData={members}
      feedItems={feedItems}
      upcomingMeetings={upcomingMeetings}
      conversationId={
        conversation === undefined ? undefined : conversation?.id ?? null
      }
      viewer={
        me === null
          ? null
          : {
              id: me.id,
              name: `${me.firstName} ${me.lastName}`.trim(),
              photoUrl: memberPhotoUrl(me) ?? null,
            }
      }
      viewerRole={viewerRole}
      joinRequests={joinRequests}
      rosterHasMore={membersQuery.hasNextPage}
      isRosterLoadingMore={membersQuery.isFetchingNextPage}
      onRosterLoadMore={() => void membersQuery.fetchNextPage()}
      feedHasMore={feedQuery.hasNextPage}
      isFeedLoadingMore={feedQuery.isFetchingNextPage}
      onFeedLoadMore={() => void feedQuery.fetchNextPage()}
      composeOnLoad={compose === true}
      joinOnLoad={join === true}
    />
  );
}
